import React from 'react';
import {
  Grid,
  FormControl,
  TextField,
  makeStyles,
  Chip,
  Box,
  Button,
  Card,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { AppTabs } from 'components';
import { PageableTable } from 'components';
import { PageableTableHeader } from 'components/PageableTable/components';
import { AuditTable } from './components';
import RolesTab from './components/RolesTab';
import PermissionsTab from './components/PermissionsTab';
import UserManagementDialog from './components/UserManagementDialog';
import {
  getTableSettingsStorageKey,
  TableSettings,
  useLocalStorageSettings,
  useDebounce,
} from 'hooks';
import { useLocation } from 'react-router-dom';
import { getUserData } from 'lib/Helper/Auth';
import {
  fetchUsers,
  fetchDetailedUsers,
  fetchUsersGroups,
  fetchRoles,
  fetchPermissions,
  fetchUserRoles,
  fetchUserExtraPermissions,
  fetchUserEffectivePermissions,
  assignRoleToUser,
  removeRoleFromUser,
  grantExtraPermission,
  revokeExtraPermission,
} from 'lib/Api/Permissions';
import {
  User,
  DetailedUser,
  PagedDetailedUsers,
  UserGroup,
  Role,
  Permission,
  UserRole,
  UserExtraPermission,
} from 'lib/Model/Permission';

type TabValue = 'users' | 'roles' | 'permissions' | 'audit-log';

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(0.25),
    fontSize: '0.75rem',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  roleCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  roleCardContent: {
    flexGrow: 1,
  },
  superuserChip: {
    marginLeft: theme.spacing(1),
  },
  roleTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  permissionsSection: {
    marginTop: theme.spacing(1),
  },
  permissionCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  permissionCardContent: {
    flexGrow: 1,
  },
  permissionTitle: {
    marginBottom: theme.spacing(1),
  },
  searchContainer: {
    marginBottom: theme.spacing(3),
  },
  permissionChips: {
    marginTop: theme.spacing(1),
  },
  userAvatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    fontSize: '1.5rem',
    fontWeight: 600,
  },
  userInfo: {
    flexGrow: 1,
  },
  userName: {
    marginBottom: theme.spacing(0.5),
  },
  userEmail: {
    color: theme.palette.text.secondary,
  },
  sectionContainer: {
    marginBottom: theme.spacing(4),
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  roleItem: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  roleItemTitle: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
  },
  roleItemHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  roleItemActions: {
    marginLeft: theme.spacing(1),
  },
  roleItemDescription: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  roleItemDate: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  alertBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.info.main}`,
    backgroundColor: theme.palette.info.light,
  },
  warningBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.warning.main}`,
    backgroundColor: theme.palette.warning.light,
  },
  dialogActions: {
    padding: theme.spacing(2),
    gap: theme.spacing(1),
  },
  noteField: {
    marginTop: theme.spacing(2),
  },
  cardContent: {
    padding: theme.spacing(2),
  },
}));

const PermissionsView: React.FC = () => {
  const classes = useStyles();
  const { pathname, hash } = useLocation();
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(
      getTableSettingsStorageKey(pathname, hash),
      { rowsPerPage: 25 },
    );

  const currentUser = getUserData();

  const [usersLoading, setUsersLoading] = React.useState(false);
  const [usersData, setUsersData] = React.useState<User[] | undefined>();
  const [selectedUsers, setSelectedUsers] = React.useState<string[]>([]);

  const [groupsLoading, setGroupsLoading] = React.useState(false);
  const [groupsData, setGroupsData] = React.useState<UserGroup[] | undefined>();
  const [selectedGroups, setSelectedGroups] = React.useState<string[]>([]);

  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);
  const [selectedPermissions, setSelectedPermissions] = React.useState<
    string[]
  >([]);

  const [detailedUsersLoading, setDetailedUsersLoading] = React.useState(false);
  const [detailedUsersData, setDetailedUsersData] = React.useState<
    PagedDetailedUsers | undefined
  >();
  const [pageNumber, setPageNumber] = React.useState(0);
  const [nameSearch, setNameSearch] = React.useState<string | undefined>();
  const debouncedNameSearch = useDebounce(nameSearch, 400);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<DetailedUser | null>(
    null,
  );
  const [userRoles, setUserRoles] = React.useState<UserRole[]>([]);
  const [userRolesLoading, setUserRolesLoading] = React.useState(false);
  const [userExtraPermissions, setUserExtraPermissions] = React.useState<
    UserExtraPermission[]
  >([]);
  const [userExtraPermissionsLoading, setUserExtraPermissionsLoading] =
    React.useState(false);
  const [userEffectivePermissions, setUserEffectivePermissions] =
    React.useState<string[]>([]);
  const [userEffectivePermissionsLoading, setUserEffectivePermissionsLoading] =
    React.useState(false);

  const [rolesData, setRolesData] = React.useState<Role[] | undefined>();
  const [addRoleDialogOpen, setAddRoleDialogOpen] = React.useState(false);
  const [selectedRoleToAdd, setSelectedRoleToAdd] = React.useState<Role | null>(
    null,
  );
  const [assigningRole, setAssigningRole] = React.useState(false);

  const [grantPermissionDialogOpen, setGrantPermissionDialogOpen] =
    React.useState(false);
  const [selectedPermissionToGrant, setSelectedPermissionToGrant] =
    React.useState<Permission | null>(null);
  const [permissionNote, setPermissionNote] = React.useState('');
  const [grantingPermission, setGrantingPermission] = React.useState(false);

  const [permissionsData, setPermissionsData] = React.useState<
    Permission[] | undefined
  >();
  const [permissionsSearch, setPermissionsSearch] = React.useState<
    string | undefined
  >();
  const debouncedPermissionsSearch = useDebounce(permissionsSearch, 400);

  const userColumns: PageableTableHeader[] = [
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'group',
      label: 'Group',
    },
    {
      key: 'position',
      label: 'Position',
    },
    {
      key: 'roles',
      label: 'Roles',
    },
    {
      key: 'permissions',
      label: 'Permissions',
    },
    {
      key: 'actions',
      label: 'Actions',
    },
  ];

  const userRows =
    detailedUsersData?.data?.map((user, index) => ({
      key: index.toString(),
      cells: [
        {
          key: 'name',
          display: user.name_surname,
        },
        {
          key: 'group',
          display: user.group_name || 'No Group',
        },
        {
          key: 'position',
          display: (
            <Chip
              label={user.position_title || 'N/A'}
              variant="outlined"
              size="small"
              className={classes.chip}
            />
          ),
        },
        {
          key: 'roles',
          display: (
            <Box className={classes.chipContainer}>
              {user.roles
                ? user.roles
                    .split(',')
                    .map((role, idx) => (
                      <Chip
                        key={idx}
                        label={role.trim()}
                        size="small"
                        className={classes.chip}
                      />
                    ))
                : null}
            </Box>
          ),
        },
        {
          key: 'permissions',
          display: (
            <Box className={classes.chipContainer}>
              {user.permissions
                ? user.permissions
                    .split(',')
                    .map((permission, idx) => (
                      <Chip
                        key={idx}
                        label={permission.trim()}
                        size="small"
                        className={classes.chip}
                        color="primary"
                      />
                    ))
                : null}
            </Box>
          ),
        },
        {
          key: 'actions',
          display: (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                setSelectedUser(user);
                setDialogOpen(true);
                fetchUserRolesData(user.google_id);
                fetchUserExtraPermissionsData(user.google_id);
                fetchUserEffectivePermissionsData(user.google_id);
              }}
            >
              Manage
            </Button>
          ),
        },
      ],
    })) ?? [];

  const tabs = [
    { label: 'Users', value: 'users' as TabValue },
    { label: 'Roles', value: 'roles' as TabValue },
    { label: 'Permissions', value: 'permissions' as TabValue },
    { label: 'Audit Log', value: 'audit-log' as TabValue },
  ];

  const fetchUsersData = React.useCallback(() => {
    setUsersLoading(true);
    fetchUsers().then((d) => {
      setUsersLoading(false);
      setUsersData(d);
    });
  }, [setUsersLoading, setUsersData]);

  const fetchGroupsData = React.useCallback(() => {
    setGroupsLoading(true);
    fetchUsersGroups().then((d) => {
      setGroupsLoading(false);
      setGroupsData(d);
    });
  }, [setGroupsLoading, setGroupsData]);

  const fetchRolesData = React.useCallback(() => {
    fetchRoles().then((d) => {
      setRolesData(d);
    });
  }, [setRolesData]);

  const fetchPermissionsData = React.useCallback(() => {
    fetchPermissions(debouncedPermissionsSearch).then((d) => {
      setPermissionsData(d);
    });
  }, [debouncedPermissionsSearch, setPermissionsData]);

  const fetchUserRolesData = React.useCallback((googleId: string) => {
    setUserRolesLoading(true);
    fetchUserRoles(googleId).then((d) => {
      setUserRolesLoading(false);
      setUserRoles(d);
    });
  }, []);

  const fetchUserExtraPermissionsData = React.useCallback(
    (googleId: string) => {
      setUserExtraPermissionsLoading(true);
      fetchUserExtraPermissions(googleId).then((d) => {
        setUserExtraPermissionsLoading(false);
        setUserExtraPermissions(d);
      });
    },
    [],
  );

  const fetchUserEffectivePermissionsData = React.useCallback(
    (googleId: string) => {
      setUserEffectivePermissionsLoading(true);
      fetchUserEffectivePermissions(googleId).then((d) => {
        setUserEffectivePermissionsLoading(false);
        setUserEffectivePermissions(d);
      });
    },
    [],
  );

  const fetchDetailedUsersData = React.useCallback(() => {
    setDetailedUsersLoading(true);
    const pageNumberToLoad = pageNumber + 1;
    const selectedUserGoogleId =
      selectedUsers.length === 1 ? selectedUsers[0] : undefined;
    const selectedTeam =
      selectedGroups.length === 1 ? selectedGroups[0] : undefined;
    const selectedRole =
      selectedRoles.length === 1 ? selectedRoles[0] : undefined;
    const selectedPermission =
      selectedPermissions.length === 1 ? selectedPermissions[0] : undefined;

    fetchDetailedUsers(
      pageNumberToLoad,
      tableSettings.rowsPerPage,
      selectedUserGoogleId,
      selectedTeam,
      debouncedNameSearch,
      selectedRole,
      selectedPermission,
    ).then((d) => {
      setDetailedUsersLoading(false);
      setDetailedUsersData(d);
    });
  }, [
    pageNumber,
    tableSettings.rowsPerPage,
    selectedUsers,
    selectedGroups,
    selectedRoles,
    selectedPermissions,
    debouncedNameSearch,
  ]);

  React.useEffect(() => {
    fetchUsersData();
  }, [fetchUsersData]);

  React.useEffect(() => {
    fetchGroupsData();
  }, [fetchGroupsData]);

  React.useEffect(() => {
    fetchRolesData();
  }, [fetchRolesData]);

  React.useEffect(() => {
    fetchPermissionsData();
  }, [fetchPermissionsData]);

  React.useEffect(() => {
    fetchDetailedUsersData();
  }, [fetchDetailedUsersData]);

  React.useEffect(() => {
    setPageNumber(0);
  }, [
    debouncedNameSearch,
    tableSettings.rowsPerPage,
    selectedUsers,
    selectedGroups,
    selectedRoles,
    selectedPermissions,
  ]);

  const handleSelectedUsersChange = (values: string[]) => {
    setSelectedUsers(values);
  };

  const handleSelectedGroupsChange = (values: string[]) => {
    setSelectedGroups(values);
  };

  const handleSelectedRolesChange = (values: string[]) => {
    setSelectedRoles(values);
  };

  const handleSelectedPermissionsChange = (values: string[]) => {
    setSelectedPermissions(values);
  };

  const sortedUserOptions = React.useMemo(() => {
    const userOptions = usersData ?? [];
    return [...userOptions].sort((a, b) => {
      const groupA = a.group_name || 'No Group';
      const groupB = b.group_name || 'No Group';
      if (groupA !== groupB) {
        return groupA.localeCompare(groupB);
      }
      return a.name_surname.localeCompare(b.name_surname);
    });
  }, [usersData]);

  const sortedGroupOptions = React.useMemo(() => {
    const groupOptions = groupsData ?? [];
    return [...groupOptions].sort((a, b) =>
      a.group_name.localeCompare(b.group_name),
    );
  }, [groupsData]);

  const sortedRoleOptions = React.useMemo(() => {
    const roleOptions = rolesData ?? [];
    return [...roleOptions].sort((a, b) => a.name.localeCompare(b.name));
  }, [rolesData]);

  const sortedPermissionOptions = React.useMemo(() => {
    const permissionOptions = permissionsData ?? [];
    return [...permissionOptions].sort((a, b) => a.name.localeCompare(b.name));
  }, [permissionsData]);

  const hasAdminRole = React.useMemo(() => {
    return userRoles.some((role) => role.is_superuser);
  }, [userRoles]);

  const handleAssignRole = React.useCallback(async () => {
    if (!selectedUser || !selectedRoleToAdd) return;

    if (
      selectedRoleToAdd.is_superuser &&
      !(currentUser?.is_superuser || false)
    ) {
      return;
    }

    setAssigningRole(true);
    try {
      await assignRoleToUser(selectedUser.google_id, selectedRoleToAdd.slug);
      setAddRoleDialogOpen(false);
      setSelectedRoleToAdd(null);
      fetchUserRolesData(selectedUser.google_id);
      fetchUserEffectivePermissionsData(selectedUser.google_id);
      fetchDetailedUsersData();
    } finally {
      setAssigningRole(false);
    }
  }, [
    selectedUser,
    selectedRoleToAdd,
    currentUser,
    fetchUserRolesData,
    fetchUserEffectivePermissionsData,
    fetchDetailedUsersData,
  ]);

  const handleGrantPermission = React.useCallback(async () => {
    if (!selectedUser || !selectedPermissionToGrant) return;

    setGrantingPermission(true);
    try {
      await grantExtraPermission(
        selectedUser.google_id,
        selectedPermissionToGrant.slug,
        permissionNote || undefined,
      );
      setGrantPermissionDialogOpen(false);
      setSelectedPermissionToGrant(null);
      setPermissionNote('');
      fetchUserExtraPermissionsData(selectedUser.google_id);
      fetchUserEffectivePermissionsData(selectedUser.google_id);
      fetchDetailedUsersData();
    } finally {
      setGrantingPermission(false);
    }
  }, [
    selectedUser,
    selectedPermissionToGrant,
    permissionNote,
    fetchUserExtraPermissionsData,
    fetchUserEffectivePermissionsData,
    fetchDetailedUsersData,
  ]);

  const handleRevokePermission = React.useCallback(
    async (permissionSlug: string) => {
      if (!selectedUser) return;

      try {
        await revokeExtraPermission(selectedUser.google_id, permissionSlug);
        fetchUserExtraPermissionsData(selectedUser.google_id);
        fetchUserEffectivePermissionsData(selectedUser.google_id);
        fetchDetailedUsersData();
      } catch (error) {}
    },
    [
      selectedUser,
      fetchUserExtraPermissionsData,
      fetchUserEffectivePermissionsData,
      fetchDetailedUsersData,
    ],
  );

  const handleRemoveRole = React.useCallback(
    async (roleSlug: string) => {
      if (!selectedUser) return;

      try {
        await removeRoleFromUser(selectedUser.google_id, roleSlug);
        fetchUserRolesData(selectedUser.google_id);
        fetchUserEffectivePermissionsData(selectedUser.google_id);
        fetchDetailedUsersData();
      } catch (error) {}
    },
    [
      selectedUser,
      fetchUserRolesData,
      fetchUserEffectivePermissionsData,
      fetchDetailedUsersData,
    ],
  );

  const handleRoleClick = React.useCallback((roleSlug: string) => {
    setSelectedRoles([roleSlug]);
    setSelectedPermissions([]);
    setSelectedUsers([]);
    setSelectedGroups([]);
    setNameSearch(undefined);
    window.location.hash = 'users';
  }, []);

  const handlePermissionClick = React.useCallback((permissionSlug: string) => {
    setSelectedPermissions([permissionSlug]);
    setSelectedRoles([]);
    setSelectedUsers([]);
    setSelectedGroups([]);
    setNameSearch(undefined);
    window.location.hash = 'users';
  }, []);

  const handleTabChange = React.useCallback(
    (
      event: React.ChangeEvent<{}> | React.FormEvent<HTMLButtonElement>,
      value?: TabValue,
    ) => {
      if (value !== undefined) {
        window.location.hash = value;
      }
    },
    [],
  );

  return (
    <AppTabs tabs={tabs} onChange={handleTabChange}>
      {(currentTab) => {
        switch (currentTab) {
          case 'users':
            return (
              <>
                <Card>
                  <Box className={classes.cardContent}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} lg={4}>
                        <FormControl fullWidth>
                          <TextField
                            variant="outlined"
                            label="Search by Name"
                            fullWidth
                            placeholder="Enter user name"
                            InputLabelProps={{ shrink: true }}
                            value={nameSearch || ''}
                            onChange={(e) =>
                              setNameSearch(e.target.value || undefined)
                            }
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} lg={4}>
                        <FormControl fullWidth>
                          <Autocomplete
                            multiple
                            loading={usersLoading}
                            groupBy={(option) =>
                              option.group_name || 'No Group'
                            }
                            getOptionLabel={(option) => option.name_surname}
                            options={sortedUserOptions}
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                label="Filter by Users"
                                {...params}
                                fullWidth
                                placeholder="All users"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            value={sortedUserOptions.filter((u) =>
                              selectedUsers.includes(u.google_id),
                            )}
                            onChange={(e, newValue) => {
                              handleSelectedUsersChange(
                                newValue.map((v) => v.google_id),
                              );
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} lg={4}>
                        <FormControl fullWidth>
                          <Autocomplete
                            multiple
                            loading={groupsLoading}
                            getOptionLabel={(option) => option.group_name}
                            options={sortedGroupOptions}
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                label="Filter by Groups"
                                {...params}
                                fullWidth
                                placeholder="All groups"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            value={sortedGroupOptions.filter((g) =>
                              selectedGroups.includes(g.group_name),
                            )}
                            onChange={(e, newValue) => {
                              handleSelectedGroupsChange(
                                newValue.map((v) => v.group_name),
                              );
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} lg={4}>
                        <FormControl fullWidth>
                          <Autocomplete
                            multiple
                            getOptionLabel={(option) => option.name}
                            options={sortedRoleOptions}
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                label="Filter by Roles"
                                {...params}
                                fullWidth
                                placeholder="All roles"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            value={sortedRoleOptions.filter((r) =>
                              selectedRoles.includes(r.slug),
                            )}
                            onChange={(e, newValue) => {
                              handleSelectedRolesChange(
                                newValue.map((v) => v.slug),
                              );
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} lg={4}>
                        <FormControl fullWidth>
                          <Autocomplete
                            multiple
                            getOptionLabel={(option) => option.name}
                            options={sortedPermissionOptions}
                            renderInput={(params) => (
                              <TextField
                                variant="outlined"
                                label="Filter by Permissions"
                                {...params}
                                fullWidth
                                placeholder="All permissions"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            value={sortedPermissionOptions.filter((p) =>
                              selectedPermissions.includes(p.slug),
                            )}
                            onChange={(e, newValue) => {
                              handleSelectedPermissionsChange(
                                newValue.map((v) => v.slug),
                              );
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                  <PageableTable
                    tableSettings={tableSettings}
                    setTableSettings={setTableSettings}
                    showSkeletonLoading={detailedUsersLoading}
                    columns={userColumns}
                    rows={userRows}
                    loading={detailedUsersLoading}
                    pageNumber={pageNumber}
                    rowCount={detailedUsersData?.total_count ?? 0}
                    onChangePage={setPageNumber}
                  />
                </Card>
                <UserManagementDialog
                  dialogOpen={dialogOpen}
                  setDialogOpen={setDialogOpen}
                  selectedUser={selectedUser}
                  userRoles={userRoles}
                  userRolesLoading={userRolesLoading}
                  userExtraPermissions={userExtraPermissions}
                  userExtraPermissionsLoading={userExtraPermissionsLoading}
                  userEffectivePermissions={userEffectivePermissions}
                  userEffectivePermissionsLoading={
                    userEffectivePermissionsLoading
                  }
                  rolesData={rolesData}
                  permissionsData={permissionsData}
                  addRoleDialogOpen={addRoleDialogOpen}
                  setAddRoleDialogOpen={setAddRoleDialogOpen}
                  selectedRoleToAdd={selectedRoleToAdd}
                  setSelectedRoleToAdd={setSelectedRoleToAdd}
                  assigningRole={assigningRole}
                  grantPermissionDialogOpen={grantPermissionDialogOpen}
                  setGrantPermissionDialogOpen={setGrantPermissionDialogOpen}
                  selectedPermissionToGrant={selectedPermissionToGrant}
                  setSelectedPermissionToGrant={setSelectedPermissionToGrant}
                  permissionNote={permissionNote}
                  setPermissionNote={setPermissionNote}
                  grantingPermission={grantingPermission}
                  hasAdminRole={hasAdminRole}
                  currentUserIsSuperuser={currentUser?.is_superuser || false}
                  handleAssignRole={handleAssignRole}
                  handleRemoveRole={handleRemoveRole}
                  handleGrantPermission={handleGrantPermission}
                  handleRevokePermission={handleRevokePermission}
                />
              </>
            );
          case 'roles':
            return (
              <RolesTab rolesData={rolesData} onRoleClick={handleRoleClick} />
            );
          case 'permissions':
            return (
              <PermissionsTab
                permissionsData={permissionsData}
                permissionsSearch={permissionsSearch}
                setPermissionsSearch={setPermissionsSearch}
                onPermissionClick={handlePermissionClick}
              />
            );
          case 'audit-log':
            return (
              <Card>
                <Box className={classes.cardContent}>
                  <AuditTable />
                </Box>
              </Card>
            );
          default:
            return null;
        }
      }}
    </AppTabs>
  );
};

export default PermissionsView;
